import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";

const CONSENT_KEY = "cookie-consent";
const CONSENT_VERSION = "1.0";
const EXPIRY_MONTHS = 6;

export interface ConsentState {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  date: string;
  version: string;
}

interface CookieConsentContextValue {
  consent: ConsentState | null;
  showBanner: boolean;
  showModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  acceptAll: () => void;
  acceptFunctionalOnly: () => void;
  savePreferences: (prefs: { analytics: boolean; marketing: boolean }) => void;
  hasConsent: boolean;
}

const defaultConsent: ConsentState = {
  functional: true,
  analytics: false,
  marketing: false,
  date: new Date().toISOString(),
  version: CONSENT_VERSION,
};

function getStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed.date || !parsed.version) return null;

    // Version check
    if (parsed.version !== CONSENT_VERSION) return null;

    // Expiry check (6 months)
    const storedDate = new Date(parsed.date);
    const expiryDate = new Date(storedDate);
    expiryDate.setMonth(expiryDate.getMonth() + EXPIRY_MONTHS);
    if (new Date() > expiryDate) return null;

    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(consent: ConsentState) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

function clearThirdPartyCookies() {
  // Clear known first-party cookies set by analytics/marketing scripts
  const domains = ["", window.location.hostname, `.${window.location.hostname}`];
  const cookieNames = ["_ga", "_gid", "_gat", "_fbp", "_gcl_au"];

  domains.forEach((domain) => {
    cookieNames.forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;${domain ? ` domain=${domain};` : ""}`;
    });
  });
}

function removeAnalyticsScripts() {
  // Remove Google Analytics / gtag scripts
  const selectors = [
    'script[src*="googletagmanager"]',
    'script[src*="google-analytics"]',
    'script[src*="gtag"]',
    'script[src*="facebook"]',
    'script[src*="fbevents"]',
    'script[src*="googleadservices"]',
    'script[src*="doubleclick"]',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });

  // Clear dataLayer and fbq if they exist
  if (typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer = undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag = undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).fbq = undefined;
  }
}

function injectAnalyticsScripts() {
  // Google Analytics 4 (gtag) — placeholder, will be injected when analytics consent is true
  if (document.querySelector('script[src*="googletagmanager"]')) return;
  const gaScript = document.createElement("script");
  gaScript.async = true;
  gaScript.src = "https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID";
  document.head.appendChild(gaScript);

  const gtagScript = document.createElement("script");
  gtagScript.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'GA_MEASUREMENT_ID', { anonymize_ip: true });
  `;
  document.head.appendChild(gtagScript);
}

function injectMarketingScripts() {
  // Meta Pixel — placeholder
  if (document.querySelector('script[src*="fbevents"]')) return;
  const fbScript = document.createElement("script");
  fbScript.text = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', 'META_PIXEL_ID');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(fbScript);
}

function applyConsentScripts(consent: ConsentState | null) {
  if (!consent) {
    removeAnalyticsScripts();
    return;
  }

  if (consent.analytics) {
    injectAnalyticsScripts();
  }

  if (consent.marketing) {
    injectMarketingScripts();
  }

  if (!consent.analytics && !consent.marketing) {
    removeAnalyticsScripts();
  }
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return ctx;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(() => getStoredConsent());
  const [showBanner, setShowBanner] = useState(() => getStoredConsent() === null);
  const [showModal, setShowModal] = useState(false);
  const prevConsentRef = useRef<ConsentState | null>(getStoredConsent());

  useEffect(() => {
    console.log("[CookieConsent] v1.0 loaded");
    applyConsentScripts(consent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply scripts whenever consent changes
  useEffect(() => {
    applyConsentScripts(consent);

    // If user revoked analytics or marketing after previously accepting, clean up
    const prev = prevConsentRef.current;
    if (prev && consent) {
      const revokedAnalytics = prev.analytics && !consent.analytics;
      const revokedMarketing = prev.marketing && !consent.marketing;
      if (revokedAnalytics || revokedMarketing) {
        clearThirdPartyCookies();
        removeAnalyticsScripts();
        // Reload to ensure clean state after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    }
    prevConsentRef.current = consent;
  }, [consent]);

  const openModal = useCallback(() => {
    setShowModal(true);
    setShowBanner(false);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    if (!consent) {
      setShowBanner(true);
    }
  }, [consent]);

  const acceptAll = useCallback(() => {
    const newConsent: ConsentState = {
      functional: true,
      analytics: true,
      marketing: true,
      date: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    storeConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const acceptFunctionalOnly = useCallback(() => {
    const newConsent: ConsentState = {
      functional: true,
      analytics: false,
      marketing: false,
      date: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    storeConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const savePreferences = useCallback(
    (prefs: { analytics: boolean; marketing: boolean }) => {
      const newConsent: ConsentState = {
        functional: true,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        date: new Date().toISOString(),
        version: CONSENT_VERSION,
      };
      storeConsent(newConsent);
      setConsent(newConsent);
      setShowBanner(false);
      setShowModal(false);
    },
    []
  );

  const value: CookieConsentContextValue = {
    consent,
    showBanner,
    showModal,
    openModal,
    closeModal,
    acceptAll,
    acceptFunctionalOnly,
    savePreferences,
    hasConsent: consent !== null,
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}